import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, Lock } from 'lucide-react';

import Button from '../../../components/ui/Button';
import { computeMissingRecommendations } from '../config';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 6px;
  width: 480px;
  max-width: 92vw;
  max-height: 88vh;
  overflow-y: auto;
  padding: 24px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #A0A8B4;
  &:hover:not(:disabled) { background: #F1F5F9; color: #004242; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const MissingBox = styled.div`
  background: #FFF7ED;
  border: 1px solid #FED7AA;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 16px;
  font-size: 12px;
  color: #9A3412;
`;

const MissingTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  margin-bottom: 6px;
`;

const MissingList = styled.ul`
  margin: 0;
  padding-left: 18px;
`;

const WarningBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 16px;
  font-size: 12px;
  color: #991B1B;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #E53935;
  margin-bottom: 12px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

// Faz G (spec 2026-08-08 §3/§4, Task 4) — kapanış diyaloğu: geri alınamaz
// (spec §3 "tur kapanır — öneriler kesinleşir"), CloseSessionDialog.js'in
// missingProposals deseniyle AYNI (eksik-öneri kilidi + BE race'i için aynı
// hata yolundan gelen liste). recommendations FinalPct/ProposedPct İKİSİ de
// boş satırları client-side computeMissingRecommendations ile ÖNCEDEN
// hesaplar (BE'nin ReadyToClose kararıyla birebir), BE yine de aynı hatayla
// dönerse (yarış durumu) Data.missing ile GÜNCELLENİR.
export default function CloseCycleDialog({ recommendations, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [missingNames, setMissingNames] = useState(computeMissingRecommendations(recommendations));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const hasMissing = missingNames.length > 0;

  const handleSubmit = async () => {
    if (hasMissing) return;
    setError('');
    setSaving(true);
    const result = await onSubmit();
    setSaving(false);

    if (result.ok) {
      onClose();
      return;
    }

    if (result.message === 'performance.compCycle.missingRecommendations') {
      setMissingNames(result.data?.missing || []);
      return;
    }
    setError(t(`responseErrors.${result.message}`, { defaultValue: result.message }));
  };

  return (
    <Overlay onClick={saving ? undefined : onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{t('compCycles.closeDialog.title')}</Title>
          <CloseBtn onClick={onClose} disabled={saving}><X size={18} /></CloseBtn>
        </Header>

        {hasMissing && (
          <MissingBox data-role="missing-recommendations-box">
            <MissingTitle><AlertTriangle size={14} /> {t('compCycles.closeDialog.missingHint')}</MissingTitle>
            <MissingList>
              {missingNames.map((name, i) => <li key={`${name}-${i}`}>{name}</li>)}
            </MissingList>
          </MissingBox>
        )}

        {!hasMissing && (
          <WarningBox data-role="close-irreversible-warning">
            <AlertTriangle size={14} />
            <span>{t('compCycles.closeDialog.warning')}</span>
          </WarningBox>
        )}

        {error && <ErrorText data-role="close-error">{error}</ErrorText>}

        <Footer>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            data-role="confirm-close-btn"
            variant="primary"
            onClick={handleSubmit}
            disabled={saving || hasMissing}
          >
            <Lock size={14} />
            {saving ? t('common.saving') : t('compCycles.closeDialog.confirmButton')}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
}
